/**
 * Specific-date list parsing for event.schedule_notes.
 *
 * Some venues (spring training stadiums, concert halls, fairs) only operate on
 * a discrete set of days inside their season range. Rather than introducing a
 * new column, we encode the play dates inline in `schedule_notes` as ISO
 * `YYYY-MM-DD` tokens. Any field with two or more such tokens is treated as
 * the authoritative list of "open" days. Single-date or zero-date notes fall
 * back to today's behavior (range overlap + day-name heuristics).
 *
 * Example value:
 *   "Game days: 2026-02-22, 2026-02-25, 2026-02-28, 2026-03-01"
 */

const ISO_DATE_RE = /\b(20\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])\b/g;

/** Extract every ISO YYYY-MM-DD token from a free-text schedule note. */
export function extractSpecificDates(notes: string | null | undefined): string[] {
  if (!notes) return [];
  const seen = new Set<string>();
  const matches = notes.match(ISO_DATE_RE);
  if (!matches) return [];
  for (const m of matches) seen.add(m);
  return Array.from(seen).sort();
}

/**
 * True when a schedule_notes field encodes an authoritative play-date list
 * (≥2 ISO dates). One date alone is more naturally `start_date`, so we don't
 * reinterpret single-date notes as a list.
 */
export function hasSpecificDates(notes: string | null | undefined): boolean {
  return extractSpecificDates(notes).length >= 2;
}

/** True if any date in the list falls inside [rangeStart, rangeEnd] (inclusive ISO compare). */
export function datesInRange(dates: string[], rangeStart: string, rangeEnd: string): string[] {
  return dates.filter((d) => d >= rangeStart && d <= rangeEnd);
}

/** Convenience: true when at least one date is in the window. */
export function matchesSelectedWindow(dates: string[], rangeStart: string, rangeEnd: string): boolean {
  return datesInRange(dates, rangeStart, rangeEnd).length > 0;
}

/**
 * Weekday parsing for free-text schedule notes.
 *
 * Returns a set of weekday indices (0=Sun … 6=Sat) the venue is open on,
 * based on tokens found in `schedule_notes`. When the notes don't contain
 * any recognizable schedule cue (e.g. "Book online", "Hours vary"), returns
 * `null` to signal "unknown — treat as always open". This is intentional:
 * we'd rather show a venue and let the user click through than wrongly
 * exclude it because of a quirky string.
 *
 * Recognized patterns (case-insensitive):
 *   - Always-open cues: "daily", "everyday", "every day", "always open",
 *     "24 hours", "24/7", "dawn to dusk", "sunrise to sunset",
 *     "open sunrise", "dawn-dusk"
 *   - Single days: "monday" / "mon" (full or 3-letter abbreviation, plus "tues", "thurs")
 *   - Day ranges: "Mon-Sat", "Thu–Sun", "Tuesday to Friday", "Mon - Fri"
 *   - Comma lists: "Tue, Thu, Sat"
 *   - "Weekdays" → Mon–Fri; "Weekends" → Sat–Sun
 */

const DAY_TO_INDEX: Record<string, number> = {
  sun: 0, sunday: 0,
  mon: 1, monday: 1,
  tue: 2, tues: 2, tuesday: 2,
  wed: 3, weds: 3, wednesday: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4,
  fri: 5, friday: 5,
  sat: 6, saturday: 6,
};

const ALWAYS_OPEN_RE = /\b(daily|everyday|every day|always open|24 ?hours?|24\/7|dawn[\s-]+to[\s-]+dusk|dawn[\s-]*-[\s-]*dusk|sunrise[\s-]+to[\s-]+sunset|open sunrise|sunrise to \d|open 24|open dawn)\b/i;

// Match "Mon-Sat", "Thu–Sun", "Tuesday to Friday" etc. The separator covers
// hyphen, en/em dash, "to", "through", "thru", "–", "—" and surrounding spaces.
const RANGE_RE = /\b(sun|sunday|mon|monday|tue|tues|tuesday|wed|weds|wednesday|thu|thur|thurs|thursday|fri|friday|sat|saturday)\s*(?:-|–|—|to|through|thru)\s*(sun|sunday|mon|monday|tue|tues|tuesday|wed|weds|wednesday|thu|thur|thurs|thursday|fri|friday|sat|saturday)\b/gi;

const SINGLE_DAY_RE = /\b(sunday|monday|tuesday|wednesday|thursday|friday|saturday|sun|mon|tues|tue|weds|wed|thurs|thur|thu|fri|sat)\b/gi;

function fillRange(start: number, end: number): number[] {
  // Forward-wrapping range: e.g. Thu(4) → Tue(2) means Thu, Fri, Sat, Sun, Mon, Tue.
  const out: number[] = [];
  let i = start;
  // Safety cap at 7 iterations.
  for (let n = 0; n < 7; n++) {
    out.push(i);
    if (i === end) return out;
    i = (i + 1) % 7;
  }
  return out;
}

/**
 * Parse `schedule_notes` and return the set of weekday indices the venue is
 * open on, or `null` when the notes don't tell us anything useful (in which
 * case callers should treat the event as always-open).
 */
export function getOpenWeekdays(notes: string | null | undefined): Set<number> | null {
  if (!notes || !notes.trim()) return null;
  const text = notes.toLowerCase();

  if (ALWAYS_OPEN_RE.test(text)) {
    return new Set([0, 1, 2, 3, 4, 5, 6]);
  }

  const result = new Set<number>();

  if (/\bweekdays?\b/.test(text)) [1, 2, 3, 4, 5].forEach((d) => result.add(d));
  if (/\bweekends?\b/.test(text)) [0, 6].forEach((d) => result.add(d));

  // Ranges first — and remove them from the working text so the single-day
  // sweep doesn't double-count their endpoints.
  let stripped = text;
  const rangeMatches = Array.from(text.matchAll(RANGE_RE));
  for (const m of rangeMatches) {
    const start = DAY_TO_INDEX[m[1].toLowerCase()];
    const end = DAY_TO_INDEX[m[2].toLowerCase()];
    if (start === undefined || end === undefined) continue;
    fillRange(start, end).forEach((d) => result.add(d));
    stripped = stripped.replace(m[0], " ");
  }

  // Remaining standalone day tokens (incl. comma lists like "Tue, Thu, Sat").
  const singleMatches = Array.from(stripped.matchAll(SINGLE_DAY_RE));
  for (const m of singleMatches) {
    const idx = DAY_TO_INDEX[m[1].toLowerCase()];
    if (idx !== undefined) result.add(idx);
  }

  return result.size > 0 ? result : null;
}

/** True when the venue is (or might be) open on the given weekday index. */
export function isOpenOnWeekday(notes: string | null | undefined, weekday: number): boolean {
  const open = getOpenWeekdays(notes);
  if (open === null) return true; // unknown → don't exclude
  return open.has(weekday);
}

/** True when the venue is (or might be) open on at least one day in the range. */
export function isOpenOnAnyWeekday(notes: string | null | undefined, weekdays: number[]): boolean {
  const open = getOpenWeekdays(notes);
  if (open === null) return true;
  return weekdays.some((d) => open.has(d));
}
